import { useEffect } from "react";
import { getPhotoUrl } from "../../../common/utils/getPhotoUrl";
import { BLOCK_UPDATE_CODE, MESSAGE_NACK_CODE, MESSAGE_SEND_ERROR } from "../constants/constants";
import { getUserInfoByUuids } from "../apis/chatApi";
import { blockedUsersQueryKey } from "../hooks/useChatBlockUsersLoad";
import { chatRoomsQueryKey } from "../hooks/useChatRoomsLoad";
import { queryClient } from "../queryClient";
import { getDisplayName, set as setCachedUser } from "../stores/userDisplayUtils";
import {
  getCurrentUserId,
  mapHistoryMessagesToUI,
  mapServerMessageToUI,
  mergeReconnectMessagesFromHistory,
} from "../utils/messageMapping";
import { updateRoomListWithMessage } from "../utils/roomListMapping";
import { WEBSOCKET_STATE } from "./webSocketState";

export function useChatWebSocketListeners({
  selectedRoomIdRef,
  setSelectedRoom,
  setCurrentRoomMessages,
  setCurrentRoomNextCursor,
  setCurrentRoomHasMore,
  historyLoadMoreRef,
  mergeReconnectRoomIdRef,
  setHistoryLoading,
  onForcedLeave,
}) {
  useEffect(() => {
    const onChatRoomCreated = (e) => {
      const { room } = e.detail ?? {};
      if (!room?.id) return;
      // rooms API 재호출 → 멤버 정보 포함된 전체 목록으로 갱신
      queryClient.invalidateQueries({ queryKey: chatRoomsQueryKey });
    };
    window.addEventListener("chatRoomCreated", onChatRoomCreated);
    return () => window.removeEventListener("chatRoomCreated", onChatRoomCreated);
  }, []);

  useEffect(() => {
    const onMessageHistoryAck = (e) => {
      const { roomId, messages, nextCursor, hasMore } = e.detail ?? {};
      if (selectedRoomIdRef?.current !== roomId) {
        if (mergeReconnectRoomIdRef?.current === roomId) mergeReconnectRoomIdRef.current = null;
        return;
      }
      const currentUserId = getCurrentUserId();
      const mapped = mapHistoryMessagesToUI(messages, currentUserId, roomId);
      const isLoadMore = historyLoadMoreRef?.current === true;
      const mergeReconnect =
        mergeReconnectRoomIdRef?.current != null && mergeReconnectRoomIdRef.current === roomId;
      if (mergeReconnect && mergeReconnectRoomIdRef) mergeReconnectRoomIdRef.current = null;

      if (isLoadMore) {
        setCurrentRoomMessages((prev) => [...mapped, ...prev]);
        if (historyLoadMoreRef) historyLoadMoreRef.current = false;
        if (setHistoryLoading) setHistoryLoading(false);
      } else if (mergeReconnect) {
        // reconnect delta가 20개 초과 → 너무 많이 밀림, 전체 새로고침
        if (mapped.length > 20) {
          setCurrentRoomMessages(mapped);
          setCurrentRoomNextCursor(nextCursor ?? null);
          setCurrentRoomHasMore(!!hasMore);
          return;
        }
        setCurrentRoomMessages((prev) => mergeReconnectMessagesFromHistory(prev, mapped));
      } else {
        setCurrentRoomMessages((prev) => {
          const serverIds = new Set(mapped.map((m) => m.id));
          const tail = prev.filter(
            (m) =>
              (m.status === WEBSOCKET_STATE.PENDING || m.status === WEBSOCKET_STATE.FAILED) &&
              m.id != null &&
              !serverIds.has(m.id)
          );
          return [...mapped, ...tail];
        });
      }
      if (!mergeReconnect) {
        setCurrentRoomNextCursor(nextCursor ?? null);
        setCurrentRoomHasMore(!!hasMore);
      }
    };
    window.addEventListener("messageHistoryAck", onMessageHistoryAck);
    return () => window.removeEventListener("messageHistoryAck", onMessageHistoryAck);
  }, [
    selectedRoomIdRef,
    setCurrentRoomMessages,
    setCurrentRoomNextCursor,
    setCurrentRoomHasMore,
    historyLoadMoreRef,
    mergeReconnectRoomIdRef,
    setHistoryLoading,
  ]);

  useEffect(() => {
    const onMessageHistoryNack = () => {
      if (historyLoadMoreRef?.current) historyLoadMoreRef.current = false;
      if (setHistoryLoading) setHistoryLoading(false);
    };
    window.addEventListener("messageHistoryNack", onMessageHistoryNack);
    return () => window.removeEventListener("messageHistoryNack", onMessageHistoryNack);
  }, [historyLoadMoreRef, setHistoryLoading]);

  useEffect(() => {
    const onMessageAck = (e) => {
      const { message, meta } = e.detail ?? {};
      if (!message?.messageId) return;
      if (selectedRoomIdRef?.current === message.roomId) {
        const mapped = mapServerMessageToUI(message, getCurrentUserId());
        if (mapped) {
          setCurrentRoomMessages((prev) => {
            const idx = prev.findIndex((m) => m.id === meta.requestId);
            if (idx >= 0) {
              const cur = prev[idx];
              const merged = {
                ...cur,
                ...mapped,
                clientRequestId: cur.clientRequestId ?? meta.requestId,
              };
              return [...prev.slice(0, idx), merged, ...prev.slice(idx + 1)];
            }
            return [...prev, mapped];
          });
        }
      }
      const myUserId = getCurrentUserId();
      const currentRooms = queryClient.getQueryData(chatRoomsQueryKey) || [];
      const roomInList = currentRooms.some((r) => r.id === message.roomId);
      queryClient.setQueryData(chatRoomsQueryKey, (prev) =>
        updateRoomListWithMessage(prev || [], message, { myUserId })
      );
      if (!roomInList) {
        queryClient.invalidateQueries({ queryKey: chatRoomsQueryKey });
      }
    };
    window.addEventListener("messageAck", onMessageAck);
    return () => window.removeEventListener("messageAck", onMessageAck);
  }, [selectedRoomIdRef, setCurrentRoomMessages]);

  useEffect(() => {
    const onMessageNack = (e) => {
      const { meta, payload } = e.detail ?? {};
      const requestId = meta?.requestId;
      const code = payload?.code;
      const failReasonMap = {
        [MESSAGE_NACK_CODE.USER_BLOCKED]: MESSAGE_SEND_ERROR.USER_BLOCKED,
        [MESSAGE_NACK_CODE.BLOCKED_BY_USER]: MESSAGE_SEND_ERROR.BLOCKED_BY_USER,
        [MESSAGE_NACK_CODE.ROOM_ACCESS_DENIED]: MESSAGE_SEND_ERROR.ROOM_ACCESS_DENIED,
        [MESSAGE_NACK_CODE.INVALID_REQUEST]: MESSAGE_SEND_ERROR.INVALID_REQUEST,
      };
      let failReason = failReasonMap[code];
      if (
        !failReason &&
        code === MESSAGE_NACK_CODE.MESSAGE_SEND_FAILED &&
        typeof payload?.message === "string" &&
        payload.message.includes("500")
      )
        failReason = MESSAGE_SEND_ERROR.MESSAGE_TOO_LONG;
      setCurrentRoomMessages((prev) => {
        if (!requestId) {
          // requestId 없음 = 연결 오류 → pending 전체 failed 처리
          return prev.some((m) => m.status === WEBSOCKET_STATE.PENDING)
            ? prev.map((m) =>
                m.status === WEBSOCKET_STATE.PENDING
                  ? { ...m, status: WEBSOCKET_STATE.FAILED, failReason }
                  : m
              )
            : prev;
        }
        const idx = prev.findIndex((m) => m.id === requestId);
        if (idx >= 0)
          return [
            ...prev.slice(0, idx),
            { ...prev[idx], status: WEBSOCKET_STATE.FAILED, failReason },
            ...prev.slice(idx + 1),
          ];
        return prev;
      });
    };
    window.addEventListener("messageNack", onMessageNack);
    return () => window.removeEventListener("messageNack", onMessageNack);
  }, [setCurrentRoomMessages]);

  useEffect(() => {
    const onMessageReceived = async (e) => {
      const { message } = e.detail ?? {};
      if (!message?.messageId) return;
      if (selectedRoomIdRef?.current === message.roomId) {
        const mapped = mapServerMessageToUI(message, getCurrentUserId());
        if (mapped) setCurrentRoomMessages((prev) => [...prev, mapped]);
      }
      const myUserId = getCurrentUserId();
      const senderId = message.senderId || message.sender_id;
      if (senderId && senderId !== myUserId && !getDisplayName(senderId)) {
        const users = await getUserInfoByUuids([senderId]);
        users.forEach((u) => {
          if (u.uuid)
            setCachedUser(u.uuid, { name: u.name || null, photo: getPhotoUrl(u.photo, "agent") });
        });
      }
      const currentRooms = queryClient.getQueryData(chatRoomsQueryKey) || [];
      const roomInList = currentRooms.some((r) => r.id === message.roomId);
      queryClient.setQueryData(chatRoomsQueryKey, (prev) =>
        updateRoomListWithMessage(prev || [], message, {
          incrementUnread: selectedRoomIdRef?.current !== message.roomId,
          myUserId,
        })
      );
      if (!roomInList) {
        queryClient.invalidateQueries({ queryKey: chatRoomsQueryKey });
      }
    };
    window.addEventListener("messageReceived", onMessageReceived);
    return () => window.removeEventListener("messageReceived", onMessageReceived);
  }, [selectedRoomIdRef, setCurrentRoomMessages]);

  useEffect(() => {
    const onRoomLeft = (e) => {
      const { roomId, userId } = e.detail ?? {};
      if (!roomId) return;
      if (userId !== getCurrentUserId()) return;
      onForcedLeave?.(roomId);
    };
    window.addEventListener("roomLeft", onRoomLeft);
    return () => window.removeEventListener("roomLeft", onRoomLeft);
  }, [onForcedLeave]);

  useEffect(() => {
    const onReadReceiptUpdated = (e) => {
      const { roomId, userId, lastReadSeq } = e.detail ?? {};
      if (!roomId || !userId) return;
      const myUserId = getCurrentUserId();
      const isMe = userId === myUserId;
      const patchRoom = (room) => {
        if (!room || room.id !== roomId) return room;
        const nextMembers = (room.members || []).map((m) =>
          m.userId === userId ? { ...m, lastReadSeq } : m
        );
        if (!isMe) return { ...room, members: nextMembers };
        const newUnread = Math.max(0, (room.lastMessageSeq ?? 0) - lastReadSeq);
        return { ...room, members: nextMembers, unreadCount: newUnread };
      };
      setSelectedRoom?.((prev) => patchRoom(prev));
      queryClient.setQueryData(chatRoomsQueryKey, (prev) => (prev || []).map((r) => patchRoom(r)));
    };
    window.addEventListener("roomReadReceiptUpdated", onReadReceiptUpdated);
    return () => window.removeEventListener("roomReadReceiptUpdated", onReadReceiptUpdated);
  }, [setSelectedRoom]);

  useEffect(() => {
    const onUserBlockUpdated = (e) => {
      const { code, userId } = e.detail ?? {};
      if (!code || !userId) return;
      const patchByCode = {
        [BLOCK_UPDATE_CODE.USER_BLOCKED]: { blockedByMe: true },
        [BLOCK_UPDATE_CODE.USER_UNBLOCKED]: { blockedByMe: false },
        [BLOCK_UPDATE_CODE.BLOCKED_BY_USER]: { blockedByOther: true },
        [BLOCK_UPDATE_CODE.UNBLOCKED_BY_USER]: { blockedByOther: false },
      };
      const patch = patchByCode[code];
      if (!patch) return;
      queryClient.setQueryData(["blockStatus", userId], (prev) => ({
        ...(prev || { blockedByMe: false, blockedByOther: false }),
        ...patch,
      }));
      queryClient.invalidateQueries({ queryKey: blockedUsersQueryKey });
      queryClient.invalidateQueries({ queryKey: chatRoomsQueryKey });
    };
    window.addEventListener("userBlockUpdated", onUserBlockUpdated);
    return () => window.removeEventListener("userBlockUpdated", onUserBlockUpdated);
  }, []);
}
